import React, { useState, useEffect } from 'react'
import Header from '../../../app/components/Header'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Flex,
  Text,
  useMediaQuery,
  NumberInput,
  NumberInputField,
  IconButton,
  Spinner,
  Switch,
  Tooltip,
} from '@chakra-ui/react'
import { EditIcon, CheckIcon } from '@chakra-ui/icons'

import { PeriodTabStrip } from '../components/PeriodTabStrip'
import {
  useGetLocationDataQuery,
  useGetLocationElectricityCostQuery,
  useUpdateLocationElectricityCostMutation,
} from '../api/dasboardApi'
import { useQueryParams } from '../hooks/useQueryParams'
import { PERIODPARAMS } from '../consts'
import { LineChart } from './lineChart/LineChart'
import { toast } from 'react-toastify'
import { LocationSummary } from './summary/LocationSummary'
import {
  getPeriodLabelFromDates,
  locationOverviewParameters,
} from '../../../utils/helpers'

export function OverviewLocation() {
  const [queryParams] = useQueryParams(PERIODPARAMS)
  const [isSmallerThan1200] = useMediaQuery('(max-width: 1200px)')
  const periodLabel = getPeriodLabelFromDates(queryParams.from, queryParams.to)
  const { data, isLoading, isFetching } = useGetLocationDataQuery(
    {
      from_date: queryParams?.from,
      to_date: queryParams?.to,
      location: queryParams?.location_id,
      group: queryParams?.group_id,
    },
    { skip: !queryParams.from || !queryParams.to },
  )

  const {
    data: costs,
    isLoading: isCostLoading,
    refetch: refetchCosts,
  } = useGetLocationElectricityCostQuery()

  const [updateCost] = useUpdateLocationElectricityCostMutation()

  const [showIbex, setShowIbex] = useState(false)
  const [cost, setCost] = useState(null)
  const [isEditing, setIsEditing] = useState(false)
  const [editedPrice, setEditedPrice] = useState('')
  const [prevPrice, setPrevPrice] = useState('')
  const [isSaving, setIsSaving] = useState(false)

  const getSafePrice = (price: string | number | null | undefined) => {
    return price !== null && price !== undefined && !isNaN(Number(price))
      ? Number(price).toFixed(2)
      : '0.00'
  }

  useEffect(() => {
    if (costs && costs.length > 0) {
      setCost(costs[0])
      setEditedPrice(getSafePrice(costs[0].price))
    }
  }, [costs])

  const handleEdit = () => {
    if (cost) {
      setPrevPrice(cost.price ?? '0')
      setEditedPrice(getSafePrice(cost.price))
      setIsEditing(true)
    }
  }

  const handleSave = async () => {
    if (!cost) return

    if (editedPrice.trim() === '' || isNaN(Number(editedPrice))) {
      toast.error('Please enter a valid price.')
      return
    }

    setIsSaving(true)

    try {
      const updated = await updateCost({
        id: cost.id,
        body: {
          ...cost,
          price: editedPrice,
          price_currency: cost.price_currency.toLowerCase(),
          price_unit: cost.price_unit.toUpperCase(),
        },
      }).unwrap()

      setCost(updated[0])
      setIsEditing(false)
      toast.success('Electricity cost updated successfully.')
      await refetchCosts()
    } catch (error) {
      toast.error('Failed to update electricity cost.')
      setEditedPrice(prevPrice)
    } finally {
      setIsSaving(false)
    }
  }

  const HeaderContent = <Header title={queryParams.title || 'Location Overview'} />

  return (
    <>
      {!isSmallerThan1200 && HeaderContent}
      <Flex direction="column" gap={5}>
        <Flex justifyContent="space-between" alignItems="center" mt={4}>
          <PeriodTabStrip />
          <Flex gap={2} alignItems="center">
            <Text fontWeight={400} color="#707070">
              Electricity cost
            </Text>

            {isCostLoading || !cost ? (
              <Spinner size="sm" />
            ) : showIbex ? (
              <Text fontWeight={700} color="#484848" fontSize="16px">
                {getSafePrice(cost.ibex_price)} {cost.price_currency?.toUpperCase()}
              </Text>
            ) : isEditing ? (
              <NumberInput
                size="sm"
                maxW="80px"
                value={editedPrice}
                onChange={(value) => setEditedPrice(value)}
                precision={2}
                step={0.01}
                clampValueOnBlur
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSave()
                  else if (e.key === 'Escape') {
                    setIsEditing(false)
                    setEditedPrice(getSafePrice(prevPrice))
                  }
                }}
              >
                <NumberInputField
                  fontWeight={700}
                  fontSize="16px"
                  color="#484848"
                  autoFocus
                />
              </NumberInput>
            ) : (
              <Text fontWeight={700} color="#484848" fontSize="16px">
                {getSafePrice(cost.price)} {cost.price_currency?.toUpperCase()}
              </Text>
            )}

            {cost && cost.price_currency && (
              <>
                <Text fontWeight={400} color="#9B9B9B" fontSize="14px">
                  /kWh
                </Text>
                <IconButton
                  aria-label={
                    isEditing ? 'Save electricity cost' : 'Edit electricity cost'
                  }
                  icon={isEditing ? <CheckIcon /> : <EditIcon />}
                  size="sm"
                  variant="ghost"
                  onClick={isEditing ? handleSave : handleEdit}
                  isLoading={isSaving}
                  isDisabled={showIbex}
                />
              </>
            )}

            <Tooltip
              hasArrow
              placement="left"
              bg="heading.dark"
              label={showIbex ? 'Showing IBEX price' : 'Showing self-input price'}
              openDelay={300}
              closeDelay={200}
            >
              <Flex align="center" justify="center">
                <Switch
                  size="sm"
                  colorScheme="blue"
                  isChecked={showIbex}
                  onChange={(e) => setShowIbex(e.target.checked)}
                />
              </Flex>
            </Tooltip>
          </Flex>
        </Flex>

        <Flex direction={{ base: 'column', desktopLg: 'row' }} gap={8}>
          <Flex
            key={queryParams.location_id || queryParams.group_id}
            width="100%"
            height="fit-content"
            gap={6}
            direction="row"
            borderRadius="xl"
            bg="white"
            boxShadow="sm"
          >
            {isLoading || isFetching ? (
              <Flex align="center" justify="center" width="100%" height="200px">
                <Spinner size="lg" />
              </Flex>
            ) : data?.length > 0 ? (
              <Flex alignItems="center" width="100%" direction="column" px={5}>
                {data
                  ?.filter((location) =>
                    locationOverviewParameters.some(
                      (x) => x === location.parameter_title,
                    ),
                  )
                  ?.map((location: any, idx: number) => (
                    <LineChart key={idx} data={location} periodLabel={periodLabel} />
                  ))}
              </Flex>
            ) : (
              <Alert
                status="info"
                bg="blue.50"
                borderRadius="md"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                textAlign="center"
                height="200px"
                color="blue.800"
              >
                <AlertIcon boxSize="40px" mr={0} color="blue.500" />
                <AlertTitle mt={4} mb={1} fontSize="lg" fontWeight="bold">
                  No data available for the selected period
                </AlertTitle>
                <AlertDescription maxWidth="sm" fontSize="sm" color="blue.700">
                  Try choosing a different date range to check for available data.
                </AlertDescription>
              </Alert>
            )}
          </Flex>

          {data?.length > 0 && <LocationSummary data={data} />}
        </Flex>
      </Flex>
    </>
  )
}
